import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Tooltip,
  IconButton,
  InputGroup,
  InputLeftElement,
  Text
} from '@chakra-ui/react';
import { DownloadIcon, SearchIcon } from '@chakra-ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { timesheetAPI, projectsAPI } from '../services/api';

const Timesheet: React.FC = () => {
  // State declarations FIRST
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [weekStart, setWeekStart] = useState('');
  const [weekEnd, setWeekEnd] = useState('');
  const [dailyHours, setDailyHours] = useState<number[]>(Array(7).fill(0));
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [search, setSearch] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [myTimesheets, setMyTimesheets] = useState<any[]>([]);
  const toast = useToast();
  const navigate = useNavigate();

  // Get billable hours for selected project
  const selectedProjectObj = projects.find((p: any) => String(p._id) === String(selectedProject));
  const billableHours = selectedProjectObj?.totalBillableHoursPerDay !== undefined ? selectedProjectObj.totalBillableHoursPerDay : '-';

  // Helper: check if note is required for each day
  const isNoteRequired = () => {
    if (!selectedProjectObj) return false;
    // Mon-Fri: idx 0-4
    for (let i = 0; i <= 4; i++) {
      if (dailyHours[i] !== selectedProjectObj.totalBillableHoursPerDay) return true;
    }
    // Sat/Sun: idx 5,6
    if (dailyHours[5] > 0 || dailyHours[6] > 0) return true;
    return false;
  };

  // Helper: error message for note
  const getNoteError = () => {
    if (!isNoteRequired()) return '';
    if (!note.trim() || note.trim().length < 20) {
      // Mon-Fri reason
      for (let i = 0; i <= 4; i++) {
        if (dailyHours[i] !== selectedProjectObj?.totalBillableHoursPerDay) {
          return `Please provide a reason (at least 20 characters) if hours on ${['Mon','Tue','Wed','Thu','Fri'][i]} are not ${selectedProjectObj?.totalBillableHoursPerDay}.`;
        }
      }
      // Sat/Sun reason
      if (dailyHours[5] > 0) return 'Please provide a reason for working on Saturday (at least 20 characters).';
      if (dailyHours[6] > 0) return 'Please provide a reason for working on Sunday (at least 20 characters).';
    }
    return '';
  };

  useEffect(() => {
    setNoteError(getNoteError());
  }, [note, dailyHours, selectedProject]);
  // Only enable submit on Friday, Saturday, Sunday
  function isSubmitDay() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
    return dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0; // Fri, Sat, Sun
    // return dayOfWeek === 1 || dayOfWeek === 2 || dayOfWeek === 0;
  }
  // Calculate current week (Monday to Sunday)
  function getCurrentWeekDates() {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
    // Calculate Monday
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dayOfWeek + 6) % 7));
    // Calculate Sunday
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return {
      monday: monday.toISOString().slice(0, 10),
      sunday: sunday.toISOString().slice(0, 10)
    };
  }
  const { monday, sunday } = getCurrentWeekDates();
  const { user } = useAuth();
  const role = user?.role;
  // ...existing code...

  // Fetch projects assigned to this associate
  useEffect(() => {
    if (user?.associateId) {
      projectsAPI.getAll().then((res: any) => {
        // Filter projects where this associate is assigned
        const assigned = res.data.filter((p: any) => Array.isArray(p.associates) && p.associates.includes(user.associateId));
        setProjects(assigned);
        setSelectedProject(''); // Always default to empty (show placeholder)
      });
      // Fetch my timesheets
      timesheetAPI.getTimesheets(user.associateId).then((res: any) => setMyTimesheets(res.data));
    }
  }, [user]);

  // Check if timesheet for selected project and current week exists
  useEffect(() => {
    if (!selectedProject || !weekStart || !weekEnd) {
      setAlreadySubmitted(false);
      return;
    }
    const exists = myTimesheets.some(ts =>
      ts.projectId === selectedProject && ts.weekStart === weekStart && ts.weekEnd === weekEnd
    );
    setAlreadySubmitted(exists);
  }, [selectedProject, weekStart, weekEnd, myTimesheets]);

  // Validation
  const validateHours = () => dailyHours.every(h => h >= 0 && h <= 24);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject || !weekStart || !weekEnd || !validateHours()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill all fields and enter valid hours (0-24) for each day.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (noteError) {
      toast({
        title: 'Note Required',
        description: noteError,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      if (!user?.associateId) throw new Error('Associate ID not found');
      const totalHours = dailyHours.reduce((sum, h) => sum + h, 0);
      await timesheetAPI.saveTimesheet({
        associateId: user.associateId,
        projectId: selectedProject,
        weekStart,
        weekEnd,
        dailyHours,
        note,
        totalHours,
      });
      setIsSubmitted(true);
      toast({
        title: 'Timesheet Submitted',
        description: 'Your timesheet has been saved.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      // Reset form fields
      setSelectedProject(projects.length > 0 ? projects[0]._id : '');
      setWeekStart('');
      setWeekEnd('');
      setDailyHours(Array(7).fill(0));
      setNote('');
      setIsSubmitted(false);
      // Refresh timesheets
      timesheetAPI.getTimesheets(user.associateId).then((res: any) => setMyTimesheets(res.data));
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to submit timesheet.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDownloadPDF = (id: number) => {
    toast({
      title: 'Download PDF',
      description: `Timesheet PDF for associate ID ${id} downloaded (mock).`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  // Filter/search logic for tables
  const filteredTimesheets = myTimesheets.filter(ts => {
    return (
      (!search || ts.name.toLowerCase().includes(search.toLowerCase()) || ts.period.includes(search)) &&
      (!filterPeriod || ts.period.toLowerCase().includes(filterPeriod.toLowerCase()))
    );
  });

  return (
    <Box p={8}>
      {/* Card 1: Submit Timesheet */}
      <Card>
        <CardHeader>
          <Heading size="md">Submit Weekly Timesheet</Heading>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Assigned Project</FormLabel>
                    <Select
                      value={selectedProject}
                      onChange={e => {
                        setSelectedProject(e.target.value);
                        setAlreadySubmitted(false);
                      }}
                      disabled={isSubmitted || projects.length === 0}
                    >
                    <option value="">Select project</option>
                    {projects.map((p: any) => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </Select>
                  {selectedProject && (
                    <Text color="blue.600" fontSize="sm" mt={2}>
                      Total Billable Hours (per day): {billableHours}
                    </Text>
                  )}
                  {alreadySubmitted && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      You have already submitted the timesheet for this project and week.
                    </Text>
                  )}
              </FormControl>
              <HStack>
                <FormControl isRequired>
                  <FormLabel>Week Start</FormLabel>
                  <Input
                    type="date"
                    value={weekStart}
                    min={monday}
                    max={monday}
                    onChange={e => setWeekStart(e.target.value)}
                    disabled={isSubmitted}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Week End</FormLabel>
                  <Input
                    type="date"
                    value={weekEnd}
                    min={sunday}
                    max={sunday}
                    onChange={e => setWeekEnd(e.target.value)}
                    disabled={isSubmitted}
                  />
                </FormControl>
              </HStack>
              <Box>
                <Text fontWeight="bold" mb={2}>Enter hours for each day:</Text>
                <HStack spacing={2}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                    <FormControl key={day} isRequired w="80px">
                      <FormLabel fontSize="sm" mb={1}>{day}</FormLabel>
                      <Input
                        type="number"
                        min={0}
                        max={24}
                        value={dailyHours[idx]}
                        onChange={e => {
                          const val = Math.max(0, Math.min(24, Number(e.target.value)));
                          setDailyHours(prev => prev.map((h, i) => i === idx ? val : h));
                        }}
                        disabled={isSubmitted}
                      />
                    </FormControl>
                  ))}
                </HStack>
              </Box>
              {/* Note field for reason on Sat/Sun */}
              <FormControl isRequired={dailyHours[5] > 0 || dailyHours[6] > 0}>
                <FormLabel>Note (required if Mon-Fri hours differ from billable, or Sat/Sun hours &gt; 0)</FormLabel>
                <Input
                  type="text"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="Enter reason for deviation or weekend work"
                  disabled={isSubmitted}
                />
                {noteError && (
                  <Text color="red.500" fontSize="sm" mt={1}>{noteError}</Text>
                )}
              </FormControl>
              <Button
                type="submit"
                colorScheme="blue"
                isDisabled={
                  isSubmitted ||
                  projects.length === 0 ||
                  !isSubmitDay() ||
                  alreadySubmitted ||
                  noteError !== ''
                }
                w="full"
              >
                Submit Timesheet
              </Button>
              {/* {alreadySubmitted && (
                <Text color="red.500" fontSize="sm" mt={2}>
                  You have already submitted the timesheet for this project .
                </Text>
              )} */}
              <Button mt={2} variant="outline" colorScheme="gray" w="full" onClick={() => navigate('/timesheet')}>
                Back to Timesheet List
              </Button>
            </VStack>
          </form>
        </CardBody>
      </Card>

  {/* ...existing code... */}

  {/* Card 3: All Associates Timesheets (Finance only) */}
  {role === 'finance' && (
        <Card mt={6}>
          <CardHeader>
            <Heading size="md">All Associates Timesheets</Heading>
          </CardHeader>
          <CardBody>
            <HStack mb={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </InputGroup>
              <Select placeholder="Filter by Period" value={filterPeriod} onChange={e => setFilterPeriod(e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
              <Button leftIcon={<DownloadIcon />} colorScheme="blue" size="sm" variant="solid">
                Bulk Download
              </Button>
            </HStack>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Period</Th>
                  <Th>Hours</Th>
                  <Th>Status</Th>
                  <Th>Download</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredTimesheets.map(ts => (
                  <Tr key={ts.id}>
                    <Td>{ts.name}</Td>
                    <Td>{ts.period}</Td>
                    <Td>{ts.hours}</Td>
                    <Td>{ts.status}</Td>
                    <Td>
                      <Tooltip label="Download PDF" placement="top">
                        <IconButton
                          aria-label="Download PDF"
                          icon={<DownloadIcon />}
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(ts.id)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

export default Timesheet;
